import { memo, useCallback, useEffect, useRef, useState } from "react";

import {
  Anchor,
  Button,
  CaratDownSVG,
  HorizontalDotsSVG,
  Icon,
  PopOut,
  useModal,
  useWindowSize,
} from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import Measure from "react-measure";

import { OWN_ORIGIN } from "@/base/base.constants";
import AssignModal from "@/components/AssignModal";
import AssignSplitMenu from "@/components/AssignSplitMenu/AssignSplitMenu";
import withTrackEvent from "@/eventStream/withTrackEvent/withTrackEvent.js";
import { API_BASE_URL } from "@/api/api.config";
import evaluateRestQuery from "@/remoteState/evaluateRestQuery";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import {
  assignButtonCss,
  assignButtonPopout,
  assignContainerCss,
  disabledButtonCss,
  dropdownCaretCss,
  ellipsisCss,
  horizontalDotsCss,
} from "./AssignButton.style.js";

const propTypes = {
  assignmentsDerivedFromViewPage: PropTypes.arrayOf(PropTypes.object),
  /* content_id derived from the V3 Content API  */
  contentId: PropTypes.string,
  contentIds: PropTypes.arrayOf(PropTypes.string),
  /* content_type derived from the V3 Content API */
  contentType: PropTypes.string,
  isContentBlock: PropTypes.bool,
  /* css to be passed to other components for custom styling */
  __css: PropTypes.object,
  /* custom button value  */
  buttonValue: PropTypes.string,
  customDropdownCaretCss: PropTypes.object,
  contentWithAdvisory: PropTypes.bool,
  __cssFor: PropTypes.object,
  anchorProps: PropTypes.object,
  buttonProps: PropTypes.object,
  horizontalOffset: PropTypes.string,
  verticalOffset: PropTypes.string,
  isInvertedColors: PropTypes.bool,
  isSmall: PropTypes.bool,
  /* recommendation_set_ids derived from the V3 Content API */
  recommendationSetIds: PropTypes.arrayOf(PropTypes.string),
  streamProperties: PropTypes.object,
  visibilityMarkers: PropTypes.arrayOf(PropTypes.object),
};

// these values represent the assignments popover
// dimensions used to calculate its placement when
// it becomes visible after an user event
const ASSIGNMENTS_POPOVER_WIDTH = 175;
const ASSIGNMENTS_POPOVER_HEIGHT = 68;

const defaultProps = {
  contentId: "",
  contentType: "",
  contentWithAdvisory: false,
  isContentBlock: false,
  isInvertedColors: false,
  isSmall: false,
  __css: {},
  customDropdownCaretCss: {},
  __cssFor: {},
  recommendationSetIds: [],
};

const ButtonWithTrackEvent = withTrackEvent(Button);
const AnchorWithTrackEvent = withTrackEvent(Anchor);

const HorizontalDots = ({
  buttonProps,
  contentId,
  isPopOutVisible,
  assignMenuButtonRef,
  recommendationSetIds,
  togglePopOutVisibility,
  streamProperties,
}) => {
  return (
    <ButtonWithTrackEvent
      flavor={Button.flavors.polarTertiary}
      ref={assignMenuButtonRef}
      __cssFor={{
        root: [horizontalDotsCss, buttonProps?.__cssFor.root],
        ...buttonProps?.__cssFor,
      }}
      id={`button-assign-${contentId}`}
      onClick={togglePopOutVisibility}
      ariaProps={{
        "aria-label": "Assignment Options",
        "aria-expanded": `${isPopOutVisible}`,
      }}
      dataProps={{ "data-testid": "assignmentOptionsButton" }}
      {...buttonProps}
      objectType="Assignment"
      actionPrefix="Initiate"
      actionName="Navigate"
      streamProperties={{
        ...streamProperties,
        description: "Open Create/ Add to Assignment dropdown menu",
        recommendation_set_ids: recommendationSetIds,
      }}
    >
      <Icon SvgComponent={HorizontalDotsSVG} __cssFor={{ root: ellipsisCss }} />
    </ButtonWithTrackEvent>
  );
};
HorizontalDots.propTypes = {
  buttonProps: PropTypes.object,
  contentId: PropTypes.string,
  isPopOutVisible: PropTypes.bool,
  assignMenuButtonRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.node }),
  ]), // PropTypes.ref is not part of PropTypes https://stackoverflow.com/a/51127130
  recommendationSetIds: PropTypes.array,
  togglePopOutVisibility: PropTypes.func,
  streamProperties: PropTypes.object,
};

const Caret = ({
  buttonProps,
  contentId,
  isPopOutVisible,
  assignMenuButtonRef,
  recommendationSetIds,
  togglePopOutVisibility,
  streamProperties,
}) => {
  return (
    <ButtonWithTrackEvent
      ariaProps={{
        "aria-label": "Assignment Options",
        "aria-expanded": `${isPopOutVisible}`,
      }}
      dataProps={{ "data-testid": "assignmentOptionsButton" }}
      ref={assignMenuButtonRef}
      flavor={Button.flavors.tertiary}
      size={Button.sizes.md}
      __cssFor={{
        Icon: { root: [dropdownCaretCss, buttonProps?.__cssFor.root] },
        ...buttonProps?.__cssFor,
      }}
      id={`button-assign-${contentId}`}
      onClick={togglePopOutVisibility}
      icon={{
        SvgComponent: CaratDownSVG,
      }}
      {...buttonProps}
      objectType="Assignment"
      actionPrefix="Initiate"
      actionName="Navigate"
      streamProperties={{
        ...streamProperties,
        description: "Open Create/ Add to Assignment dropdown menu",
        recommendation_set_ids: recommendationSetIds,
      }}
    />
  );
};
Caret.propTypes = {
  buttonProps: PropTypes.object,
  contentId: PropTypes.string,
  isPopOutVisible: PropTypes.bool,
  assignMenuButtonRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.node }),
  ]), // PropTypes.ref is not part of PropTypes https://stackoverflow.com/a/51127130
  recommendationSetIds: PropTypes.array,
  togglePopOutVisibility: PropTypes.func,
  streamProperties: PropTypes.object,
};

const AssignText = ({
  anchorProps,
  createAssignmentUrl,
  assignTextLinkRef,
  recommendationSetIds,
  dataProps,
  streamProperties,
  visibilityMarkers,
  isLiteUser,
}) => {
  const disabled =
    !!visibilityMarkers?.find(({ reason }) => reason === "PREMIUM_CONTENT") &&
    isLiteUser;

  const disabledProps = {
    ariaProps: {
      "aria-disabled": true,
      role: "link",
    },
  };

  return (
    <AnchorWithTrackEvent
      __cssFor={
        disabled
          ? disabledButtonCss
          : { root: [assignButtonCss, anchorProps?.__css] }
      }
      styleAnchorAsButton={{
        flavor: Button.flavors.tertiary,
        size: Button.sizes.md,
      }}
      {...(disabled ? disabledProps : { href: createAssignmentUrl })}
      ref={assignTextLinkRef}
      objectType="Assignment"
      actionPrefix="Initiate"
      actionName="Navigate"
      streamProperties={{
        ...streamProperties,
        description: "Open Create Assignment page",
        recommendation_set_ids: recommendationSetIds,
      }}
      dataProps={dataProps}
    >
      Assign
    </AnchorWithTrackEvent>
  );
};
AssignText.propTypes = {
  anchorProps: PropTypes.object,
  assignTextLinkRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.node }),
  ]),
  createAssignmentUrl: PropTypes.string,
  dataProps: PropTypes.object,
  isLiteUser: PropTypes.bool,
  recommendationSetIds: PropTypes.array,
  streamProperties: PropTypes.object,
  visibilityMarkers: PropTypes.arrayOf(PropTypes.object),
};

const AssignButton = ({
  assignmentsDerivedFromViewPage,
  contentIds,
  anchorProps,
  buttonProps,
  contentId,
  contentWithAdvisory,
  contentType,
  __cssFor,
  isContentBlock,
  isInvertedColors,
  isSmall,
  recommendationSetIds,
  streamProperties,
  visibilityMarkers,
}) => {
  const { data: user } = useUser();
  const isContentCard = !isContentBlock;
  const { isStudent, isTeacher, isParent, isLiteUser } =
    getUserProperties(user);

  const { asPath } = useRouter();
  const returnUrl = `${OWN_ORIGIN}/` + asPath;

  const [isPopOutVisible, setIsPopOutVisible] = useState(false);
  const [contentIdsToAssign, setContentIdsToAssign] = useState([]);
  const [modalProps, setModalActive, addToAssignmentModalTriggerRef] =
    useModal(false);
  const windowSize = useWindowSize();
  const assignMenuButtonRef = useRef(null);
  const assignTextLinkRef = useRef(null);
  const createAssignmentButtonRef = useRef(null);

  // Put focus on first item when pop out become visible
  useEffect(() => {
    if (!isPopOutVisible) return;
    setTimeout(() => {
      createAssignmentButtonRef.current?.focus();
    }, 0);
  }, [isPopOutVisible]);

  // We need to prevent mouseDown event to trigger onBlur handler above
  // This only happen on Safari and it's a known bug
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const togglePopOutVisibility = useCallback(() => {
    if (!modalProps.isActive) {
      setIsPopOutVisible(!isPopOutVisible);
    }
  }, [isPopOutVisible, modalProps.isActive]);

  /**
   * Close popout on focus out
   */
  const handleFocusOut = useCallback(
    (event) => {
      if (!isPopOutVisible) return;
      // Check if user is tabbing inside menu options
      if (
        event.relatedTarget === createAssignmentButtonRef.current ||
        event.relatedTarget === addToAssignmentModalTriggerRef.current
      )
        return;
      // At this point user is tabbing out of menu
      togglePopOutVisibility();
    },
    [isPopOutVisible],
  );

  const openModal = useCallback(() => {
    togglePopOutVisibility();
    setModalActive(true);
  });

  const closeModal = useCallback(() => {
    setModalActive(false);
  });

  // If we're assigning a text set, we need to be able to pass all of the member IDs
  // to the assignments editor as well. Check to see what type of content we're dealing
  // with and augment the list of assignable IDs if necessary.
  // TODO: The assignments editor *should* be able to handle this functionality, which
  // would prevent us from having to make a whole ton of calls to the content API
  // to retrieve data we probably aren't using 99% of the time. Investigate having the
  // editor retrieve member IDs based off of the "createdFrom" parameter.
  // If we ARE doing a new DeckOfCards component, we should also consider maybe moving
  // this there as a data requirement.
  useEffect(() => {
    const getContentIdsToAssign = async () => {
      const assignableIds = [contentId];
      if (!isStudent && !contentIds && contentType === "textset") {
        const contentRequest = new Request(
          `${API_BASE_URL}/api/v3/content/${contentId}/?depth=1&shape=outline`,
        );
        const { data: textSetData } = await evaluateRestQuery(contentRequest);
        const textSetMemberIds = (content, contentId) => {
          if (content?.members) {
            const contentMembers = content?.members.map(
              (member) => member.content_id,
            );
            return contentMembers.length ? contentMembers : [contentId];
          }
          return [contentId];
        };
        assignableIds.concat(
          contentIds || textSetMemberIds(textSetData, contentId),
        );
      }
      setContentIdsToAssign(assignableIds);
    };

    getContentIdsToAssign();
  }, [contentId, contentType, contentIds]);
  const encodedComma = "%2c";
  const urlContentIds = contentIdsToAssign.join(encodedComma);
  const createAssignmentUrl = `/assignments/create?return_url=${returnUrl}&createdFrom=${contentId}&contentIds=${urlContentIds}`;

  if (!(isTeacher || isParent)) {
    return null;
  }

  const renderAssignMenuUI = () => {
    const dataQaSelector = contentWithAdvisory
      ? "cat_card_assign_button"
      : "card_assign_button";
    const renderAssignTextAnchor = (
      <AssignText
        anchorProps={anchorProps}
        createAssignmentUrl={createAssignmentUrl}
        assignTextLinkRef={assignTextLinkRef}
        recommendationSetIds={recommendationSetIds}
        dataProps={{ "data-qa-selector": `${dataQaSelector}_text` }}
        streamProperties={streamProperties}
        visibilityMarkers={visibilityMarkers}
        isLiteUser={isLiteUser}
      />
    );

    // Condition removes the assign button pop up and redirects to create assignment flow for Lite users
    // Also removes "add to assignment" options from text sets since we do not currently support adding
    // text sets to an existing assignment.
    if (isContentCard && (isLiteUser || contentType === "textset")) {
      return renderAssignTextAnchor;
    }

    return isContentCard ? (
      <>
        {renderAssignTextAnchor}
        <Caret
          buttonProps={buttonProps}
          contentId={contentId}
          isPopOutVisible={isPopOutVisible}
          assignMenuButtonRef={assignMenuButtonRef}
          recommendationSetIds={recommendationSetIds}
          togglePopOutVisibility={togglePopOutVisibility}
          dataProps={{ "data-qa-selector": `${dataQaSelector}_caret` }}
          streamProperties={streamProperties}
        />
      </>
    ) : (
      // HorizontalDots is an artifact left over from the CE hero row days.
      // For context, CE hero row uses horizontal dots in place of the assign
      // button, but we rolled back that feature for teachers only. Since
      // students don't see the AssignButton, HorizontalDots is never used,
      // and will be refactored out eventually.
      <HorizontalDots
        buttonProps={buttonProps}
        contentId={contentId}
        isInvertedColors={isInvertedColors}
        isSmall={isSmall}
        isPopOutVisible={isPopOutVisible}
        assignMenuButtonRef={assignMenuButtonRef}
        recommendationSetIds={recommendationSetIds}
        togglePopOutVisibility={togglePopOutVisibility}
        dataProps={{
          "data-qa-selector": `${dataQaSelector}_horizontal_dots`,
        }}
        streamProperties={streamProperties}
      />
    );
  };

  return (
    <div css={[assignContainerCss, __cssFor.root]}>
      {renderAssignMenuUI()}
      <Measure bounds client offset>
        {({ measureRef, contentRect }) => {
          let horizontalOffset = "0px";
          let verticalOffset = "-40px";
          if (
            isPopOutVisible &&
            contentRect.bounds?.left + ASSIGNMENTS_POPOVER_WIDTH >
              windowSize.width
          ) {
            horizontalOffset = "-80px";
          }
          if (
            isPopOutVisible &&
            contentRect.bounds?.top + ASSIGNMENTS_POPOVER_HEIGHT >
              windowSize.height
          ) {
            verticalOffset = "-80px";
          }
          return (
            <div ref={measureRef}>
              <PopOut
                ariaProps={{ "aria-labelledby": `button-assign-${contentId}` }}
                isVisible={isPopOutVisible}
                onClickOff={togglePopOutVisibility}
                onEsc={togglePopOutVisibility}
                horizontalOffset={horizontalOffset || horizontalOffset}
                verticalOffset={verticalOffset || verticalOffset}
                isAnimated={false}
                __cssFor={{
                  root: assignButtonPopout,
                }}
              >
                <div
                  onBlur={handleFocusOut}
                  onMouseDown={handleMouseDown}
                  style={{ width: `${ASSIGNMENTS_POPOVER_WIDTH}px` }}
                >
                  <AssignSplitMenu
                    contentId={contentId}
                    openModal={openModal}
                    addToAssignmentButtonRef={addToAssignmentModalTriggerRef}
                    createAssignmentButtonRef={createAssignmentButtonRef}
                    createAssignmentUrl={createAssignmentUrl}
                    contentWithAdvisory={contentWithAdvisory}
                    streamProperties={streamProperties}
                  />
                </div>
              </PopOut>
            </div>
          );
        }}
      </Measure>
      {modalProps.isActive && (
        <AssignModal
          assignmentsDerivedFromViewPage={assignmentsDerivedFromViewPage}
          contentMemberIds={contentIdsToAssign}
          createAssignmentUrl={createAssignmentUrl}
          modalProps={modalProps}
          handleClickToCloseModal={closeModal}
          recommendationSetIds={recommendationSetIds}
        />
      )}
    </div>
  );
};

AssignButton.defaultProps = defaultProps;
AssignButton.propTypes = propTypes;
export default memo(AssignButton);
